import React, { createContext, useContext, useState, useEffect } from 'react';
import { createTheme } from '@mui/material/styles';

// Theme-specific branding assets (extendable for future assets like favicons)
const themeBranding = {
  siki: {
    logo: require('../assets/common/resources/SIKI_Beta.png'),
    logoAlt: 'SIKI Logo',
    appName: 'SIKI',
    favicon: require('../images/favicon.png'),
    faviconType: 'image/png',
    loading: {
      icon: require('../assets/common/resources/Sikibot.png'),
      alt: 'Loading SIKI experience',
      message: 'Loading your SIKI experience...',
      submessage: 'We are preparing everything for you.',
    },
    homeScreens: {
      app1: require('../assets/mobile-screenshots/screen1.jpg'),
      app2: require('../assets/mobile-screenshots/screen2.jpg'),
      app3: require('../assets/mobile-screenshots/screen3.jpg'),
      app4: require('../assets/mobile-screenshots/screen4.jpg'),
    },
    homeFaqBanner: require('../assets/brands/siki/home/faq-banner.png'),
    homePremiumBanner: require('../assets/brands/siki/home/premium-banner.png'),
    homeMascot: require('../assets/brands/siki/home/mascot.png'),
  },
  kensei: {
    logo: require('../assets/common/resources/FullLogo_Transparent_NoBuffer.png'),
    logoAlt: 'Kensei',
    appName: 'Kensei',
    favicon: require('../assets/common/resources/Kensei-Logo.png'),
    faviconType: 'image/png',
    loading: {
      icon: require('../assets/common/resources/Kensei-Logo.png'),
      alt: 'Loading Kensei',
      message: 'Brightening your Kensei journey...',
      submessage: 'Tuning fresh tracks for daylight mode.',
    },
  },
  memehouse: {
    logo: require('../assets/brands/memehouse/logo.png'),
    logoAlt: 'MemeHouse',
    appName: 'MemeHouse',
    favicon: require('../assets/brands/memehouse/logo.png'),
    faviconType: 'image/png',
    loading: {
      icon: require('../assets/brands/memehouse/logo.png'),
      alt: 'Loading MemeHouse',
      message: 'Loading MemeHouse...',
      submessage: 'Preparing the green room.',
    },
    homeScreens: {
      app1: require('../assets/brands/memehouse/home/app1.jpg'),
      app2: require('../assets/brands/memehouse/home/app2.jpg'),
      app3: require('../assets/brands/memehouse/home/app3.jpg'),
      app4: require('../assets/brands/memehouse/home/app4.jpg'),
    },
    homeFaqBanner: require('../assets/brands/memehouse/home/faq-banner.jpg'),
    homePremiumBanner: require('../assets/brands/memehouse/home/premium-banner.jpg'),
    homeMascot: require('../assets/brands/memehouse/home/mascot.png'),
  },
};

const fallbackBranding = themeBranding.siki;

// Get theme from environment variable or default to 'siki'
const getThemeFromEnv = () => {
  const envTheme = process.env.REACT_APP_THEME;
  const validThemes = ['siki', 'kensei', 'memehouse'];
  return validThemes.includes(envTheme) ? envTheme : 'siki';
};

// Theme configurations
const themes = {
  siki: {
    name: 'SIKI',
    palette: {
      mode: 'dark',
      primary: { main: '#FFEE20', contrastText: '#000000' },
      secondary: { main: '#1E1E1E', contrastText: '#ffffff' },
      success: { main: '#0B964B', contrastText: '#ffffff' },
      accent: { main: '#262626', contrastText: '#ffffff' },
      default: { main: '#ffffff', contrastText: '#000000' },
      background: { default: '#121212', paper: '#121212' },
      text: { primary: '#FFFFFF', secondary: '#B0B0B0' },
    },
    typography: {
      fontFamily: "'Inter', sans-serif",
      subtitle1: { fontSize: 12 },
      body1: { fontWeight: 500, fontSize: 14 },
      body2: { fontWeight: 400, color: '#FFEE20' },
      button: { textTransform: 'none' },
    },
    custom: { // Custom properties for our app
      accent: { main: '#262626', contrastText: '#ffffff' },
      default: { main: '#ffffff', contrastText: '#000000' },
      text: { accent: '#FFEE20' },
      loader: { main: '#FFEE20' },
    },
    branding: themeBranding.siki,
  },
  kensei: {
    name: 'Kensei',
    palette: {
      mode: 'light',
      primary: {
        main: '#1976d2',
        contrastText: '#ffffff',
      },
      secondary: {
        main: '#dc004e',
        contrastText: '#ffffff',
      },
      success: {
        main: '#2e7d32',
        contrastText: '#ffffff',
      },
      accent: {
        main: '#f5f5f5',
        contrastText: '#000000',
      },
      default: {
        main: '#000000',
        contrastText: '#ffffff',
      },
      background: {
        default: '#ffffff',
        paper: '#f5f5f5',
      },
      text: {
        primary: '#000000',
        secondary: '#666666',
      },
    },
    typography: {
      fontFamily: "'Inter', sans-serif",
      subtitle1: {
        fontSize: 12,
      },
      body1: {
        fontWeight: 500,
        fontSize: 14,
      },
      body2: {
        fontWeight: 400,
        color: '#1976d2',
      },
      button: {
        textTransform: 'none',
      },
    },
    // Custom properties for our app
    custom: {
      accent: {
        main: '#f5f5f5',
        contrastText: '#000000',
      },
      default: {
        main: '#000000',
        contrastText: '#ffffff',
      },
      text: {
        accent: '#1976d2',
      },
      loader: { main: '#000000' },
    },
    branding: themeBranding.kensei,
  },
  memehouse: {
    name: 'MemeHouse',
    palette: {
      mode: 'dark',
      primary: { main: '#05F29B', contrastText: '#050505' },
      secondary: { main: '#18D992', contrastText: '#050505' },
      success: { main: '#18D992', contrastText: '#050505' },
      accent: { main: '#3FA676', contrastText: '#050505' },
      default: { main: '#FFFFFF', contrastText: '#000000' },
      background: { default: '#262626', paper: '#262626' },
      text: { primary: '#FFFFFF', secondary: '#B0B0B0' },
    },
    typography: {
      fontFamily: "'Inter', sans-serif",
      subtitle1: { fontSize: 12 },
      body1: { fontWeight: 500, fontSize: 14 },
      body2: { fontWeight: 400, color: '#05F29B' },
      button: { textTransform: 'none' },
    },
    custom: {
      accent: { main: '#3FA676', contrastText: '#050505' },
      default: { main: '#FFFFFF', contrastText: '#000000' },
      text: { accent: '#05F29B' },
      loader: { main: '#05F29B' },
    },
    branding: themeBranding.memehouse,
  },
};

const mergeThemeConfig = (baseConfig = {}, overrideConfig = {}) => {
  const merged = {
    ...baseConfig,
    ...overrideConfig,
    palette: {
      ...baseConfig?.palette,
      ...overrideConfig?.palette,
    },
    typography: {
      ...baseConfig?.typography,
      ...overrideConfig?.typography,
    },
    custom: {
      ...baseConfig?.custom,
      ...overrideConfig?.custom,
    },
  };

  if (!merged.branding) {
    merged.branding = overrideConfig?.branding || baseConfig?.branding;
  }

  return merged;
};

const buildThemeConfig = (themeName) => {
  return mergeThemeConfig(themes.siki, themes[themeName] || {});
};

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [currentThemeName, setCurrentThemeName] = useState(getThemeFromEnv());
  const [muiTheme, setMuiTheme] = useState(() => {
    const initialThemeName = getThemeFromEnv();
    const themeConfig = buildThemeConfig(initialThemeName);
    return createTheme(themeConfig);
  });

  // Initialize theme from environment variable (build-time selection)
  useEffect(() => {
    const envTheme = getThemeFromEnv();
    setCurrentThemeName(envTheme);
  }, []);

  // Update MUI theme when current theme changes
  useEffect(() => {
    const themeConfig = buildThemeConfig(currentThemeName);
    const newMuiTheme = createTheme(themeConfig);
    setMuiTheme(newMuiTheme);
    
    // Update CSS custom properties for non-MUI components
    updateCSSVariables(themeConfig);
  }, [currentThemeName]);

  const hexToRgb = (color, fallback = '255, 255, 255') => {
    if (!color || typeof color !== 'string') return fallback;
    if (color.startsWith('rgb')) {
      const match = color.match(/rgba?\(([^)]+)\)/);
      return match ? match[1] : fallback;
    }
    let hex = color.replace('#', '');
    if (hex.length === 3) {
      hex = hex.split('').map(ch => ch + ch).join('');
    }
    if (hex.length !== 6) return fallback;
    const intVal = parseInt(hex, 16);
    const r = (intVal >> 16) & 255;
    const g = (intVal >> 8) & 255;
    const b = intVal & 255;
    return `${r}, ${g}, ${b}`;
  };

  const resolveAssetUrl = (asset) => {
    if (!asset) return null;
    if (typeof asset === 'string') return asset;
    if (typeof asset === 'object' && asset.default) return asset.default;
    return asset;
  };

  const updateBrandingMetadata = (branding = {}) => {
    if (typeof document === 'undefined') return;

    const envSuffix = process.env.REACT_APP_SIKI_ENV;
    const version = process.env.REACT_APP_SIKI_VERSION;

    if (branding.appName) {
      let title = branding.appName;
      if (envSuffix && envSuffix !== 'prod') {
        title += ` - ${envSuffix}`;
      }
      if (version) {
        title += ` - ${version}`;
      }
      document.title = title;
    }

    const faviconUrl = resolveAssetUrl(branding.favicon);
    if (faviconUrl) {
      const ensureLink = (rel) => {
        let link = document.querySelector(`link[rel='${rel}']`);
        if (!link) {
          link = document.createElement('link');
          link.setAttribute('rel', rel);
          document.head.appendChild(link);
        }
        return link;
      };

      const faviconLink = ensureLink('icon');
      faviconLink.setAttribute('href', faviconUrl);
      faviconLink.setAttribute('type', branding.faviconType || 'image/png');
      if (branding.faviconSizes) {
        faviconLink.setAttribute('sizes', branding.faviconSizes);
      } else {
        faviconLink.removeAttribute('sizes');
      }

      const shortcutIconLink = ensureLink('shortcut icon');
      shortcutIconLink.setAttribute('href', faviconUrl);
      shortcutIconLink.setAttribute('type', branding.faviconType || 'image/png');
      if (branding.faviconSizes) {
        shortcutIconLink.setAttribute('sizes', branding.faviconSizes);
      } else {
        shortcutIconLink.removeAttribute('sizes');
      }

      if (branding.appleTouchIcon) {
        const appleTouchLink = ensureLink('apple-touch-icon');
        appleTouchLink.setAttribute('href', resolveAssetUrl(branding.appleTouchIcon));
      }
    }
  };

  const updateCSSVariables = (themeConfig) => {
    const root = document.documentElement;
    const palette = themeConfig?.palette || {};
    const custom = themeConfig?.custom || {};
    const themeBranding = themeConfig?.branding || {};
    const branding = {
      ...fallbackBranding,
      ...themeBranding,
      loading: { ...(fallbackBranding.loading || {}), ...(themeBranding.loading || {}) },
    };

    // Set CSS custom properties
    const primaryMain = palette.primary?.main || '#FFEE20';
    const secondaryMain = palette.secondary?.main || '#1E1E1E';
    const backgroundDefault = palette.background?.default || (palette.mode === 'dark' ? '#000000' : '#FFFFFF');
    const paperBackground = palette.background?.paper || (palette.mode === 'dark' ? '#1E1E1E' : '#F5F5F5');

    root.style.setProperty('--primary-color', primaryMain);
    root.style.setProperty('--secondary-color', secondaryMain);
    root.style.setProperty('--accent-color', custom.accent?.main || primaryMain);
    root.style.setProperty('--background-color', backgroundDefault);
    root.style.setProperty('--paper-color', paperBackground);
    root.style.setProperty('--text-primary', palette.text?.primary || (palette.mode === 'dark' ? '#FFFFFF' : '#000000'));
    root.style.setProperty('--text-secondary', palette.text?.secondary || (palette.mode === 'dark' ? '#B0B0B0' : '#666666'));
    root.style.setProperty('--primary-color-rgb', hexToRgb(primaryMain, '255, 238, 32'));
    root.style.setProperty('--secondary-color-rgb', hexToRgb(secondaryMain, '30, 30, 30'));
    root.style.setProperty('--background-color-rgb', hexToRgb(backgroundDefault, palette.mode === 'dark' ? '0, 0, 0' : '255, 255, 255'));
    root.style.setProperty('--paper-color-rgb', hexToRgb(paperBackground, palette.mode === 'dark' ? '23, 21, 21' : '245, 245, 245'));
    root.style.setProperty('--text-primary-rgb', hexToRgb(palette.text?.primary, palette.mode === 'dark' ? '255, 255, 255' : '0, 0, 0'));
    root.style.setProperty('--text-secondary-rgb', hexToRgb(palette.text?.secondary, palette.mode === 'dark' ? '176, 176, 176' : '102, 102, 102'));
    
    // Set icon filter based on theme mode
    const iconFilter = palette.mode === 'dark' ? 'brightness(0) invert(1)' : 'brightness(0) invert(0)';
    root.style.setProperty('--icon-filter', iconFilter);
    
    // Set semantic colors for notifications (fallbacks cover themes without explicit palette entries)
    root.style.setProperty('--error-color', palette.error?.main || '#F20606');
    root.style.setProperty('--success-color', palette.success?.main || '#0B964B');
    root.style.setProperty('--warning-color', palette.warning?.main || '#FF9800');
    root.style.setProperty('--info-color', palette.info?.main || '#2196F3');
    root.style.setProperty('--primary-contrast-text', palette.primary?.contrastText || '#000000');
    root.style.setProperty('--primary-contrast-text-rgb', hexToRgb(palette.primary?.contrastText, '0, 0, 0'));
    const loaderAccent = custom.loader?.main || primaryMain;
    root.style.setProperty('--loader-accent-color', loaderAccent);
    
    // Set background color rgba variants for overlays and gradients
    const bgColor = palette.background.default;
    const bgColorRgba = bgColor === '#ffffff' ? 'rgba(255, 255, 255, 0.8)' : 
                       bgColor === '#000000' ? 'rgba(0, 0, 0, 0.8)' : 
                       `${bgColor}CC`; // Add alpha channel
    root.style.setProperty('--background-color-rgba', bgColorRgba);

    if (branding.logo) {
      root.style.setProperty('--theme-logo', `url(${branding.logo})`);
    }

    updateBrandingMetadata(branding);
    if (typeof window !== 'undefined') {
      window.__brandName = branding.appName || 'SIKI';
    }

    // Update body class for theme-specific styling
    document.body.className = document.body.className.replace(/theme-\w+/g, '');
    document.body.classList.add(`theme-${palette.mode}`);
    document.body.classList.add(`theme-${currentThemeName}`);
  };

  // Theme switching is disabled - theme is set at build time
  const changeTheme = (themeName) => {
    console.warn('Theme switching is disabled. Theme must be set at build time using REACT_APP_THEME environment variable.');
  };

  const getLoadingConfig = () => {
    const branding = buildThemeConfig(currentThemeName)?.branding || fallbackBranding;
    return branding.loading || fallbackBranding.loading || {};
  };

  const getAvailableThemes = () => {
    return Object.keys(themes).map(key => {
      const config = buildThemeConfig(key);
      return {
        key,
        name: config.name,
        mode: config.palette?.mode,
      };
    });
  };

  const getCurrentTheme = () => {
    return buildThemeConfig(currentThemeName);
  };

  const getCurrentBranding = () => {
    const themeBranding = buildThemeConfig(currentThemeName).branding || {};
    return {
      ...fallbackBranding,
      ...themeBranding,
      loading: { ...(fallbackBranding.loading || {}), ...(themeBranding.loading || {}) },
    };
  };

  const getThemeAsset = (key) => {
    const themeBranding = buildThemeConfig(currentThemeName).branding || {};
    const value = themeBranding[key];
    return value != null ? value : fallbackBranding[key];
  };

  const value = {
    currentTheme: muiTheme,
    currentThemeName,
    changeTheme,
    getAvailableThemes,
    getCurrentTheme,
    getCurrentBranding,
    getThemeAsset,
    getLoadingConfig,
    themes,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
